import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Modal from '../components/Modal';
import TenantForm from '../components/TenantForm';
import DateInput, { formatDateDMY } from '../components/DateInput';
import api from '../api';
import { tenantCreateErrorMessage, tenantLinkErrorMessage } from '../utils/tenantErrors';

function parseIsraeliDate(val) {
  if (!val) return null;
  const s = String(val).trim().replace(/\//g, '.');
  const parts = s.split('.');
  if (parts.length !== 3) return null;
  let day = parseInt(parts[0], 10);
  let month = parseInt(parts[1], 10);
  let year = parseInt(parts[2], 10);
  if (!year) return null;
  if (year < 100) year = 1900 + year;
  if (!day) day = 1;
  if (!month) month = 1;
  return new Date(year, month - 1, day);
}

const TENANT_TYPE_MAP = {
  0: 'בעלים', 1: 'משכירים', 2: 'מדינת ישראל', 3: 'איש קשר', 4: 'אפוטרופוס',
  'בעלים': 'בעלים', 'משכירים': 'משכירים', 'מדינת_ישראל': 'מדינת ישראל', 'אישקשר': 'איש קשר', 'אפוטרופוס': 'אפוטרופוס', 'חלקה מיוחדת': 'חלקה מיוחדת', 'חנות/משרד': 'חנות/משרד'
};

const STATUS_MAP = {
  2:  { label: 'יש להשלים חתימה של הסכם ראשוני',               bg: '#fef9c3', color: '#854d0e' },
  3:  { label: 'דיור ציבורי',                                   bg: '#e0e7ff', color: '#3730a3' },
  4:  { label: 'מסרב לשתף פעולה',                              bg: '#fee2e2', color: '#991b1b' },
  5:  { label: 'בטיפול',                                        bg: '#fef9c3', color: '#854d0e' },
  6:  { label: 'לא נוצר קשר',                                   bg: '#f3f4f6', color: '#374151' },
  7:  { label: 'חתום על הסכם סופי',                             bg: '#dcfce7', color: '#166534' },
  8:  { label: 'חתום עם בעיית רישום',                           bg: '#ffedd5', color: '#9a3412' },
  9:  { label: 'יש להשלים חתימה',                               bg: '#fef9c3', color: '#854d0e' },
  10: { label: 'קיבל הסכם',                                     bg: '#dbeafe', color: '#1e40af' },
  12: { label: 'יש לתאם חתימה',                                 bg: '#fef9c3', color: '#854d0e' },
  13: { label: 'תואמה חתימה',                                   bg: '#dcfce7', color: '#166534' },
  14: { label: 'רוצים לחתום אך יש בעיה משפטית',                bg: '#fee2e2', color: '#991b1b' },
  15: { label: 'חתום על נון שופ - יהודה לוי',                  bg: '#dcfce7', color: '#166534' },
  16: { label: 'חתום על הסכם סופי - יהודה לוי',                bg: '#dcfce7', color: '#166534' },
  17: { label: 'חתום על הסכם סופי עם הערת אזהרה - יהודה לוי',  bg: '#dcfce7', color: '#166534' },
  18: { label: 'לא יודע על מה חתום - יהודה לוי',               bg: '#f3f4f6', color: '#374151' },
  19: { label: 'מגיע לכנס הצגת הסכם סקייליין',                 bg: '#dbeafe', color: '#1e40af' },
  20: { label: 'גר בחוץ לארץ',                                  bg: '#e0e7ff', color: '#3730a3' },
};

function SortableTh({ col, label, sortCol, sortDir, onClick, style }) {
  const active = sortCol === col;
  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => onClick(col)}
      title="לחץ כדי למיין"
    >
      {label}{arrow}
    </th>
  );
}

function StatusBadge({ value }) {
  const s = STATUS_MAP[value];
  if (!s) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 10, fontSize: '0.78rem',
      fontWeight: 'bold', background: s.bg, color: s.color, whiteSpace: 'nowrap'
    }}>
      {s.label}
    </span>
  );
}

export default function Buildings() {
  const { projectName } = useParams();
  const [buildings, setBuildings] = useState([]);
  const [openRows, setOpenRows] = useState({});
  // Deep link from the events page: ?editTenant=<id>&addr=..&num=.. opens that tenant's edit modal.
  const deepLink = useRef(new URLSearchParams(window.location.search));
  const pendingEditId = useRef(deepLink.current.get('editTenant'));
  const [selectedAddress, setSelectedAddress] = useState(() => deepLink.current.get('addr') || sessionStorage.getItem(`building_addr_${projectName}`) || '');
  const [selectedNumber, setSelectedNumber] = useState(() => deepLink.current.get('num') || sessionStorage.getItem(`building_num_${projectName}`) || '');
  const [apartments, setApartments] = useState([]);
  const [editTenant, setEditTenant] = useState(null);
  const [addTenant, setAddTenant] = useState(false);
  const [addTenantApt, setAddTenantApt] = useState('');
  const [confirmTenant, setConfirmTenant] = useState(null);
  const [deleteTenant, setDeleteTenant] = useState(null);
  const [callApt, setCallApt] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  function getLocalDate() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  const [callForm, setCallForm] = useState({ phoneCallNote: '', followUpDate: getLocalDate() });
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [addBuildingForm, setAddBuildingForm] = useState({ buildingAddress: '', buildingNumber: '', parcel: '', section: '', stage: '' });
  const [confirmAddBuilding, setConfirmAddBuilding] = useState(null);
  const [addBuildingError, setAddBuildingError] = useState('');
  const [newBldgTabuParsing, setNewBldgTabuParsing] = useState(false);
  // Lawyer compare state
  const [showLawyerCompare, setShowLawyerCompare] = useState(false);
  const [lawyerFile, setLawyerFile] = useState(null);
  const [lawyerComparing, setLawyerComparing] = useState(false);
  const [lawyerMsg, setLawyerMsg] = useState('');
  // Tabu import state
  const [showTabu, setShowTabu] = useState(false);
  const [tabuParsed, setTabuParsed] = useState(null);
  const [tabuImportForm, setTabuImportForm] = useState({ buildingAddress: '', buildingNumber: '', gush: '', chelka: '' });
  const [tabuParsing, setTabuParsing] = useState(false);
  const [tabuImporting, setTabuImporting] = useState(false);
  const [tabuMsg, setTabuMsg] = useState('');
  const [showEditBuilding, setShowEditBuilding] = useState(false);
  const [editBuildingForm, setEditBuildingForm] = useState({ parcel: '', section: '', stage: '' });
  const [confirmEditBuilding, setConfirmEditBuilding] = useState(null);
  const [showAddApt, setShowAddApt] = useState(false);
  const [addAptForm, setAddAptForm] = useState({ apartmentNumber: '', floor: '', subParcel: '' });
  const [addTenantAptFields, setAddTenantAptFields] = useState({ newAptNumber: '', floor: '', subParcel: '' });
  const [confirmAddApt, setConfirmAddApt] = useState(null);
  const [editApt, setEditApt] = useState(null);
  const [editAptForm, setEditAptForm] = useState({ floor: '', subParcel: '', isNorth: false, isEast: false, isSouth: false, isWest: false });
  const [confirmEditApt, setConfirmEditApt] = useState(null);
  const [googleDriveFolderId, setGoogleDriveFolderId] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const navigate = useNavigate();

  function toggleSort(col) {
    if (sortCol === col) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortCol(null); setSortDir('asc'); }
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  function getSortValue(row, col) {
    switch (col) {
      case 'building': return `${row.buildingAddress || ''} ${String(row.buildingNumber || '').padStart(6, '0')}`;
      case 'apartmentNumber': return Number(row.apartmentNumber) || 0;
      case 'subParcel': return Number(row.subParcel) || 0;
      case 'firstName': return row.firstName || '';
      case 'lastName': return row.lastName || '';
      case 'idNumber': return row.id || '';
      case 'cellularPhone': return row.cellularPhone || '';
      case 'tenantType': return TENANT_TYPE_MAP[row.tenantType] || '';
      case 'percentage': return Number(row.percentageOfHOldingInApartment) || 0;
      case 'isContactPerson': return row.isContactPerson ? 1 : 0;
      case 'is_representative': return row.is_representative ? 1 : 0;
      case 'tenantStatus': {
        const n = Number(row.tenantStatus);
        return Number.isFinite(n) ? n : -1;
      }
      case 'lastCallDate': return row.lastCallDate ? new Date(row.lastCallDate).getTime() : 0;
      default: return '';
    }
  }

  useEffect(() => {
    fetchBuildings();
    api.get('/projects').then(res => {
      const project = res.data.find(p => p.projectName === decodeURIComponent(projectName));
      if (project?.googleDriveFolderId) {
        setGoogleDriveFolderId(project.googleDriveFolderId);
        sessionStorage.setItem('googleDriveFolderId', project.googleDriveFolderId);
      }
    });
  }, [projectName]);

  useEffect(() => {
    sessionStorage.setItem(`building_addr_${projectName}`, selectedAddress);
    if (!selectedAddress) {
      setSelectedNumber('');
      setApartments([]);
    }
  }, [selectedAddress]);

  useEffect(() => {
    sessionStorage.setItem(`building_num_${projectName}`, selectedNumber);
  }, [selectedNumber]);

  useEffect(() => {
    if (selectedAddress && selectedNumber) {
      fetchApartments();
    } else {
      setApartments([]);
    }
  }, [selectedAddress, selectedNumber]);

  useEffect(() => {
    if (!pendingEditId.current || !apartments.length) return;
    for (const apt of apartments) {
      const t = (apt.tenants || []).find(x => String(x.id) === String(pendingEditId.current));
      if (t) {
        pendingEditId.current = null;
        openEditTenant({ ...t, apartmentNumber: apt.apartmentNumber, subParcel: apt.subParcel, lastCallDate: apt.lastCallDate, lastCallDescription: apt.lastCallDescription, apt });
        break;
      }
    }
  }, [apartments]);

  async function fetchBuildings() {
    const res = await api.get(`/buildings?projectName=${encodeURIComponent(projectName)}`);
    setBuildings(res.data);
  }

  async function fetchApartments() {
    const res = await api.get(`/apartments/${encodeURIComponent(selectedAddress)}/${selectedNumber}/with-tenants`);
    setApartments(res.data);
  }

  async function handleSaveTenant(form) {
    try {
      await api.put(`/tenants/${editTenant.id}`, form);
      await offerSignAllOwners(form);
      await api.post(`/portal/access-exception/${form.id || editTenant.id}`, { enabled: !!form.hasPortalAccess }).catch(() => {});
      setEditTenant(null);
      fetchApartments();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בעדכון דייר');
    }
  }

  // Signed the final agreement with a date: offer to mark the other owners of the apartment as signed on the same date
  async function offerSignAllOwners(form) {
    const date = form.tentantDateOfSignature;
    if (String(form.tenantStatus) !== '7' || !date) return;
    const isOwner = t => ['בעלים', '0', 0].includes(t.tenantType);
    const others = (editTenant.apt?.tenants || []).filter(t => String(t.id) !== String(editTenant.id) && isOwner(t));
    if (!others.length) return;
    const shown = String(date).slice(0, 10).split('-').reverse().join('/');
    const names = others.map(t => `${t.firstName} ${t.lastName}`).join(', ');
    const all = window.confirm(
      `לסמן גם את שאר בעלי הדירה (${names}) כ"חתום על הסכם סופי" בתאריך ${shown}?\n\nאישור = כן, לשנות את כולם\nביטול = לשנות רק את ${form.firstName} ${form.lastName}`
    );
    if (!all) return;
    for (const t of others) {
      await api.put(`/tenants/${t.id}`, { ...t, tenantStatus: '7', tentantDateOfSignature: date });
    }
  }

  async function openEditTenant(row) {
    let hasPortalAccess = false;
    try {
      const r = await api.get(`/portal/access-exception/${row.id}`);
      hasPortalAccess = !!r.data.hasAccess;
    } catch {}
    setEditTenant({ ...row, hasPortalAccess });
  }

  function handleAddTenant(form) {
    if (!addTenantApt) { alert('יש להזין מספר דירה'); return; }
    setConfirmTenant(form);
  }

  async function handleConfirmAdd() {
    // Create new apartment if newAptNumber was filled
    if (addTenantAptFields.newAptNumber) {
      const aptExists = apartments.some(a => String(a.apartmentNumber) === String(addTenantAptFields.newAptNumber));
      if (!aptExists) {
        try {
          await api.post('/apartments', {
            apartmentNumber: addTenantAptFields.newAptNumber,
            floor: addTenantAptFields.floor || null,
            subParcel: addTenantAptFields.subParcel || null,
            buildingAddress: selectedAddress,
            buildingNumber: selectedNumber
          });
        } catch (err) {
          alert('שגיאה ביצירת הדירה: ' + (err.response?.data?.error || err.message));
          return;
        }
      }
    }
    try {
      await api.post('/tenants', confirmTenant);
    } catch (err) {
      alert(tenantCreateErrorMessage(err, confirmTenant.id));
      return;
    }
    try {
      await api.post('/tenants/apartment', {
        id: confirmTenant.id,
        buildingAddress: selectedAddress,
        buildingNumber: selectedNumber,
        apartmentNumber: addTenantApt
      });
    } catch (err) {
      alert(tenantLinkErrorMessage(err));
      return;
    }
    setConfirmTenant(null);
    setAddTenant(false);
    setAddTenantApt('');
    setAddTenantAptFields({ newAptNumber: '', floor: '', subParcel: '' });
    fetchApartments();
  }

  async function handleConfirmDelete() {
    await api.delete(`/tenants/apartment/${encodeURIComponent(selectedAddress)}/${selectedNumber}/${deleteTenant.apartmentNumber}/${deleteTenant.id}`);
    setDeleteTenant(null);
    fetchApartments();
  }

  useEffect(() => {
    if (search.length >= 2) {
      const timeout = setTimeout(() => {
        api.get(`/tenants/search/${encodeURIComponent(projectName)}?q=${encodeURIComponent(search)}`)
          .then(res => setSearchResults(res.data))
          .catch(() => setSearchResults([]));
      }, 300);
      return () => clearTimeout(timeout);
    } else {
      setSearchResults(null);
    }
  }, [search]);

  useEffect(() => {
    if (callApt) {
      setCallForm({ phoneCallNote: '', followUpDate: getLocalDate() });
      api.get(`/phonecalls/${encodeURIComponent(selectedAddress)}/${selectedNumber}/${callApt.apartmentNumber}`)
        .then(res => setCallHistory(res.data))
        .catch(() => setCallHistory([]));
    }
  }, [callApt]);

  async function handleAddCall(e) {
    e.preventDefault();
    await api.post('/phonecalls', {
      buildingAddress: selectedAddress,
      buildingNumber: selectedNumber,
      apartmentNumber: callApt.apartmentNumber,
      phoneCallDate: getLocalDate(),
      phoneCallNote: callForm.phoneCallNote,
      followUpDate: callForm.followUpDate || null,
      workerFirstName: localStorage.getItem('workerFirstName') || '',
      workerLastName: localStorage.getItem('workerLastName') || ''
    });
    setCallForm({ phoneCallNote: '', followUpDate: '' });
    const res = await api.get(`/phonecalls/${encodeURIComponent(selectedAddress)}/${selectedNumber}/${callApt.apartmentNumber}`);
    setCallHistory(res.data);
    fetchApartments();
  }

  // --- Building handlers ---
  async function handleTabuForNewBuilding(e) {
    const file = e.target.files[0];
    if (!file) return;
    setNewBldgTabuParsing(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await api.post('/buildings/tabu/parse', fd);
      const { gush, chelka, address } = res.data;
      setAddBuildingForm(f => ({
        ...f,
        ...(address ? { buildingAddress: address } : {}),
        ...(gush ? { parcel: gush } : {}),
        ...(chelka ? { section: chelka } : {}),
      }));
    } catch (err) {
      console.error('tabu parse error', err);
    } finally {
      setNewBldgTabuParsing(false);
    }
  }

  function handleAddBuildingSubmit(e) {
    e.preventDefault();
    const { buildingAddress, buildingNumber, parcel, section, stage } = addBuildingForm;
    if (!buildingAddress || !buildingNumber || !parcel || !section || !stage) {
      setAddBuildingError('לא מילאת את כל הפרטים');
      return;
    }
    setAddBuildingError('');
    setConfirmAddBuilding({ ...addBuildingForm, projectName });
  }
  async function handleConfirmAddBuilding() {
    await api.post('/buildings', confirmAddBuilding);
    setConfirmAddBuilding(null);
    setShowAddBuilding(false);
    setAddBuildingForm({ buildingAddress: '', buildingNumber: '', parcel: '', section: '', stage: '' });
    fetchBuildings();
  }
  function openEditBuilding() {
    const b = buildings.find(b => b.buildingAddress === selectedAddress && String(b.buildingNumber) === String(selectedNumber));
    if (!b) return;
    setEditBuildingForm({ parcel: b.parcel || '', section: b.section || '', stage: b.stage || '', tabuUrl: b.tabuUrl || '' });
    setShowEditBuilding(true);
  }
  function handleEditBuildingSubmit(e) {
    e.preventDefault();
    setConfirmEditBuilding({ ...editBuildingForm });
  }
  async function handleConfirmEditBuilding() {
    await api.put(`/buildings/${encodeURIComponent(selectedAddress)}/${selectedNumber}`, confirmEditBuilding);
    setConfirmEditBuilding(null);
    setShowEditBuilding(false);
    fetchBuildings();
  }

  // --- Tabu handlers ---
  async function handleTabuFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setNewBldgTabuParsing(true); setTabuMsg(''); setTabuParsed(null);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      fd.append('projectName', decodeURIComponent(projectName));
      const r = await api.post('/buildings/tabu/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTabuParsed(r.data);
      setTabuImportForm({
        buildingAddress: r.data.address || '',
        buildingNumber: '',
        gush: r.data.gush || '',
        chelka: r.data.chelka || ''
      });
    } catch (err) {
      setTabuMsg('שגיאה בחילוץ הנסח: ' + (err.response?.data?.error || err.message));
    }
    setNewBldgTabuParsing(false);
  }
  async function handleTabuImport() {
    if (!tabuImportForm.buildingAddress || !tabuImportForm.buildingNumber || !tabuImportForm.gush || !tabuImportForm.chelka) {
      setTabuMsg('יש למלא רחוב, מספר בניין, גוש וחלקה לפני הייבוא'); return;
    }
    setTabuImporting(true); setTabuMsg('');
    try {
      const payload = {
        projectName,
        buildingAddress: tabuImportForm.buildingAddress,
        buildingNumber: tabuImportForm.buildingNumber,
        gush: tabuImportForm.gush,
        chelka: tabuImportForm.chelka,
        apartments: tabuParsed.apartments
      };
      const r = await api.post('/buildings/tabu/import', payload);
      setTabuMsg(`✓ יובאו ${r.data.importedApts} דירות ו-${r.data.importedOwners} בעלים`);
      fetchBuildings();
      fetchApartments();
      setTimeout(() => { setShowTabu(false); setTabuParsed(null); setTabuMsg(''); }, 3000);
    } catch (err) {
      setTabuMsg('שגיאת ייבוא: ' + (err.response?.data?.error || err.message));
    }
    setTabuImporting(false);
  }

  // --- Apartment handlers ---
  function handleAddAptSubmit(e) {
    e.preventDefault();
    if (!addAptForm.apartmentNumber) return;
    setConfirmAddApt({ ...addAptForm, buildingAddress: selectedAddress, buildingNumber: selectedNumber });
  }
  async function handleConfirmAddApt() {
    await api.post('/apartments', confirmAddApt);
    setConfirmAddApt(null);
    setShowAddApt(false);
    setAddAptForm({ apartmentNumber: '', floor: '', subParcel: '' });
    fetchApartments();
  }
  function openEditApt(apt) {
    setEditApt(apt);
    setEditAptForm({ floor: apt.floor || '', subParcel: apt.subParcel || '', isNorth: !!apt.isNorth, isEast: !!apt.isEast, isSouth: !!apt.isSouth, isWest: !!apt.isWest });
  }
  function handleEditAptSubmit(e) {
    e.preventDefault();
    setConfirmEditApt({ ...editAptForm, apartmentNumber: editApt.apartmentNumber });
  }
  async function handleConfirmEditApt() {
    await api.put(`/apartments/${encodeURIComponent(selectedAddress)}/${selectedNumber}/${confirmEditApt.apartmentNumber}`, {
      floor: confirmEditApt.floor || null,
      subParcel: confirmEditApt.subParcel || null,
      isNorth: confirmEditApt.isNorth ? 1 : 0,
      isEast: confirmEditApt.isEast ? 1 : 0,
      isSouth: confirmEditApt.isSouth ? 1 : 0,
      isWest: confirmEditApt.isWest ? 1 : 0
    });
    setConfirmEditApt(null);
    setEditApt(null);
    fetchApartments();
  }

  // Flatten: one row per tenant (skip apartments with no tenants)
  const allRows = apartments.flatMap(apt =>
    apt.tenants.map((t, idx) => ({ ...t, apartmentNumber: apt.apartmentNumber, subParcel: apt.subParcel, lastCallDate: apt.lastCallDate, lastCallDescription: apt.lastCallDescription, apt, isFirstInApt: idx === 0, aptTenantCount: apt.tenants.length }))
  );

  // If search is active, use project-wide results from server; otherwise use local building data
  const baseRows = searchResults !== null
    ? searchResults.map(r => ({ ...r, apt: null, isFirstInApt: true, aptTenantCount: 1 }))
    : allRows;

  const filteredRows = baseRows.filter(row => {
    const matchesStatus = !filterStatus || String(row.tenantStatus) === filterStatus;
    return matchesStatus;
  });

  const isSearchMode = searchResults !== null;
  let rows = filteredRows;
  if (sortCol) {
    const sorted = [...filteredRows].sort((a, b) => {
      const av = getSortValue(a, sortCol);
      const bv = getSortValue(b, sortCol);
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), 'he');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    if (isSearchMode) {
      rows = sorted.map(r => ({ ...r, isFirstInApt: true, aptTenantCount: 1 }));
    } else {
      rows = sorted.map((r, i) => {
        const isFirst = i === 0 || sorted[i - 1].apartmentNumber !== r.apartmentNumber;
        let count = 1;
        if (isFirst) {
          for (let j = i + 1; j < sorted.length && sorted[j].apartmentNumber === r.apartmentNumber; j++) count++;
        }
        return { ...r, isFirstInApt: isFirst, aptTenantCount: count };
      });
    }
  }

  const addresses = [...new Set(buildings.map(b => b.buildingAddress))].sort();
  const numbers = buildings
    .filter(b => b.buildingAddress === selectedAddress)
    .map(b => b.buildingNumber)
    .sort();

  return (
    <div>
      <Navbar title={`פרויקט: ${decodeURIComponent(projectName)}`} breadcrumb={<><span style={{ color: '#93c5fd', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/projects')}>פרויקטים</span> {'>'} בניינים</>} />
      <div className="container">
        <div className="buildings-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 className="page-title">בחר בניין</h2>
          <div className="buildings-header-actions" style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => setShowAddBuilding(true)}>+ הוסף בניין</button>
            {selectedAddress && selectedNumber && <button className="btn btn-secondary" onClick={() => { setShowLawyerCompare(true); setLawyerFile(null); setLawyerMsg(''); }}>⚖️ השוואה עם עו"ד</button>}
            <button className="btn btn-secondary" onClick={() => navigate(`/project-hub/${projectName}`)}>← חזרה</button>
          </div>
        </div>

        {/* Dropdowns */}
        <div className="inline-form" style={{ marginBottom: 24 }}>
          <div className="inline-form-row">
            <div className="form-group">
              <label>רחוב</label>
              <select value={selectedAddress} onChange={e => setSelectedAddress(e.target.value)}>
                <option value="">-- בחר רחוב --</option>
                {addresses.map(addr => (
                  <option key={addr} value={addr}>{addr}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>מספר בניין</label>
              <select value={selectedNumber} onChange={e => setSelectedNumber(e.target.value)} disabled={!selectedAddress}>
                <option value="">-- בחר מספר --</option>
                {numbers.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            {selectedAddress && selectedNumber && (
              <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end', marginBottom: 4 }}>
                <button className="btn btn-secondary" onClick={openEditBuilding}>ערוך בניין</button>
                <button className="btn btn-secondary" onClick={async () => {
                  const b = buildings.find(b => b.buildingAddress === selectedAddress && String(b.buildingNumber) === String(selectedNumber));
                  const FOLDER = '1zw5K8hg3fgcL9tG_O8gLC1w4NA60ZtAG';
                  const KEY = 'AIzaSyCEO4WwOyGwRCqlJuwmV6Fi6M1empCYEjg';
                  const driveSearch = async (q) => {
                    const r = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&key=${KEY}&fields=files(id,name)`);
                    const d = await r.json();
                    return d.files || [];
                  };
                  let files = [];
                  if (b?.parcel && b?.section) {
                    files = await driveSearch(`name contains '${b.parcel}-${b.section}' and '${FOLDER}' in parents`);
                  }
                  if (!files.length && b?.buildingAddress) {
                    files = await driveSearch(`name contains '${b.buildingAddress}' and '${FOLDER}' in parents`);
                  }
                  if (!files.length && b?.buildingAddress && b?.buildingNumber) {
                    files = await driveSearch(`name contains '${b.buildingNumber}' and name contains '${b.buildingAddress.split(' ')[0]}' and '${FOLDER}' in parents`);
                  }
                  if (files.length > 0) {
                    window.open(`https://drive.google.com/file/d/${files[0].id}/view`, '_blank');
                  } else {
                    alert(`לא נמצא נסח טאבו עבור ${b?.buildingAddress} ${b?.buildingNumber}`);
                  }
                }}>צפייה בנסח טאבו</button>
              </div>
            )}
          </div>
        </div>

        {/* Tenants table */}
        {selectedAddress && selectedNumber && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 style={{ color: '#1a56db', margin: 0 }}>
                  דירות — {selectedAddress} {selectedNumber}
                </h3>
                {(() => {
                  const b = buildings.find(b => b.buildingAddress === selectedAddress && String(b.buildingNumber) === String(selectedNumber));
                  return (b?.parcel || b?.section) ? (
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a4a8a', marginTop: 3 }}>
                      {b.parcel ? `גוש: ${b.parcel}` : ''}
                      {b.parcel && b.section ? ' | ' : ''}
                      {b.section ? `חלקה: ${b.section}` : ''}
                    </div>
                  ) : null;
                })()}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={() => { setAddTenant(true); setAddTenantApt(''); setAddTenantAptFields({ newAptNumber: '', floor: '', subParcel: '' }); }}>+ הוסף דייר</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                placeholder="חיפוש לפי שם, ת.ז., דירה..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: '1 1 200px', minWidth: 180 }}
              />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 180 }}>
                <option value="">כל הסטטוסים</option>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              {(search || filterStatus) && (
                <button className="btn btn-secondary" onClick={() => { setSearch(''); setSearchResults(null); setFilterStatus(''); }}>
                  נקה סינון
                </button>
              )}
            </div>
            {rows.length === 0 ? (
              <p>אין דיירים</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="tenants-table" style={{ fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'center' }}>
                      {searchResults !== null && <SortableTh col="building" label="בניין" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />}
                      <SortableTh col="apartmentNumber" label="דירה" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="subParcel" label="ת״ח" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="firstName" label="שם פרטי" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="lastName" label="שם משפחה" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="idNumber" label="ת.ז." sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="cellularPhone" label="טלפון נייד" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="tenantType" label="סוג בעל דירה" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="percentage" label="א.אחזקה" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="isContactPerson" label="א.קשר" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="is_representative" label="נציג" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="tenantStatus" label="סטטוס" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ whiteSpace: 'nowrap' }} />
                      <SortableTh col="lastCallDate" label="סיכום שיחה אחרונה" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} style={{ minWidth: 160, maxWidth: 220 }} />
                      <th style={{ whiteSpace: 'nowrap' }}>עדכן</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const isNewApt = i > 0 && rows[i - 1].apartmentNumber !== row.apartmentNumber;
                      const rowKey = `${row.apartmentNumber}-${row.id}-${i}`;
                      const isOpen = !!openRows[rowKey];
                      return (
                      <tr key={rowKey} className={isOpen ? 'open' : ''} style={isNewApt ? { borderTop: '2px solid #111' } : {}}>
                        {searchResults !== null && <td data-label="בניין" style={{ whiteSpace: 'nowrap' }}>{row.buildingAddress} {row.buildingNumber}</td>}
                        <td data-label="דירה">
                          <strong
                            onClick={() => row.isFirstInApt && row.apt && openEditApt(row.apt)}
                            style={row.isFirstInApt && row.apt ? { color: '#1a56db', cursor: 'pointer', textDecoration: 'underline' } : {}}
                          >{row.apartmentNumber}</strong>
                        </td>
                        <td className="td-extra" data-label="ת״ח">{row.subParcel}</td>
                        <td data-label="שם פרטי">
                          <span
                            onClick={() => openEditTenant(row)}
                            style={{ color: '#1a56db', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {row.firstName}
                          </span>
                        </td>
                        <td data-label="שם משפחה">{row.lastName}</td>
                        <td className="td-extra" data-label="ת.ז.">{row.id}</td>
                        <td data-label="טלפון נייד" style={{ whiteSpace: 'nowrap' }}>
                          {row.cellularPhone}
                          {row.cellularPhone && (
                            <a
                              href={`https://wa.me/972${row.cellularPhone.replace(/^0/, '').replace(/\D/g, '')}`}
                              target="_blank" rel="noreferrer"
                              style={{ marginRight: 6, fontSize: '0.8rem', color: '#16a34a' }}
                            >💬</a>
                          )}
                        </td>
                        <td className="td-toggle" data-label="" onClick={() => setOpenRows(o => ({ ...o, [rowKey]: !o[rowKey] }))}>
                          <span style={{ color: '#1a56db', fontSize: '0.85rem', cursor: 'pointer' }}>
                            {isOpen ? 'פחות פרטים ▲' : 'עוד פרטים ▼'}
                          </span>
                        </td>
                        <td className="td-extra" data-label="סוג בעל דירה">{TENANT_TYPE_MAP[row.tenantType] || row.tenantType}</td>
                        <td className="td-extra" data-label="א.אחזקה">{row.percentageOfHOldingInApartment}</td>
                        <td className="td-extra" data-label="א.קשר" style={{ textAlign: 'center' }}>{row.isContactPerson ? '✓' : ''}</td>
                        <td className="td-extra" data-label="נציג" style={{ textAlign: 'center' }}>{row.is_representative ? '✓' : ''}</td>
                        <td className="td-extra" data-label="סטטוס"><StatusBadge value={row.tenantStatus} /></td>
                        {row.isFirstInApt && (
                          <td className="td-extra" data-label="סיכום שיחה אחרונה" rowSpan={row.aptTenantCount} style={{ color: '#6b7280', wordBreak: 'break-word', verticalAlign: 'middle' }}>
                            {row.lastCallDate && (
                              <span style={{ fontWeight: 'bold', color: '#374151', marginLeft: 4 }}>
                                {formatDateDMY(row.lastCallDate)}
                              </span>
                            )}
                            {row.lastCallDescription}
                          </td>
                        )}
                        {row.isFirstInApt && (
                          <td className="td-extra" data-label="" rowSpan={row.aptTenantCount} style={{ verticalAlign: 'middle' }}>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '3px 10px', fontSize: '0.78rem' }}
                              onClick={() => setCallApt(row.apt)}
                            >
                              עדכן
                            </button>
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {addTenant && !confirmTenant && (
        <Modal title="הוספת דייר חדש" onClose={() => setAddTenant(false)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="form-group">
              <label>מספר דירה</label>
              <input type="number" value={addTenantAptFields.newAptNumber} onChange={e => setAddTenantAptFields(f => ({ ...f, newAptNumber: e.target.value }))} placeholder="מספר דירה חדשה" />
            </div>
            <div className="form-group">
              <label>קומה</label>
              <input type="number" value={addTenantAptFields.floor} onChange={e => setAddTenantAptFields(f => ({ ...f, floor: e.target.value }))} placeholder="קומה" />
            </div>
            <div className="form-group">
              <label>תת חלקה</label>
              <input type="number" value={addTenantAptFields.subParcel} onChange={e => setAddTenantAptFields(f => ({ ...f, subParcel: e.target.value }))} placeholder="תת חלקה" />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="button" className="btn btn-primary" style={{ width: '100%' }} onClick={async () => {
                if (!addTenantAptFields.newAptNumber) { alert('יש להזין מספר דירה'); return; }
                const aptExists = apartments.some(a => String(a.apartmentNumber) === String(addTenantAptFields.newAptNumber));
                if (aptExists) { alert('דירה ' + addTenantAptFields.newAptNumber + ' כבר קיימת'); return; }
                try {
                  await api.post('/apartments', { apartmentNumber: addTenantAptFields.newAptNumber, floor: addTenantAptFields.floor || null, subParcel: addTenantAptFields.subParcel || null, buildingAddress: selectedAddress, buildingNumber: selectedNumber });
                  setAddTenantAptFields(f => ({ ...f, newAptNumber: '', floor: '', subParcel: '' }));
                  await fetchApartments();
                  alert('דירה נוספה בהצלחה');
                } catch (err) { alert('שגיאה: ' + (err.response?.data?.error || err.message)); }
              }}>שמור</button>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>מספר דירה</label>
            <select value={addTenantApt} onChange={e => setAddTenantApt(e.target.value)}>
              <option value="">-- בחר דירה --</option>
              {apartments.map(a => (
                <option key={a.apartmentNumber} value={a.apartmentNumber}>{a.apartmentNumber}</option>
              ))}
            </select>
          </div>
          <TenantForm onSubmit={handleAddTenant} onCancel={() => setAddTenant(false)} />
        </Modal>
      )}

      {confirmTenant && (
        <Modal title="אישור הוספת דייר" onClose={() => setConfirmTenant(null)}>
          <p style={{ marginBottom: 16, fontWeight: 600, color: '#0f2d55' }}>אנא אמת/י את הפרטים לפני השמירה:</p>
          <table style={{ fontSize: '0.88rem', marginBottom: 20, width: '100%' }}>
            <tbody>
              {[
                ['דירה (בחירה)', addTenantApt],
                ['דירה חדשה', addTenantAptFields.newAptNumber],
                ['קומה', addTenantAptFields.floor],
                ['תת חלקה', addTenantAptFields.subParcel],
                ['תעודת זהות', confirmTenant.id],
                ['שם פרטי', confirmTenant.firstName],
                ['שם משפחה', confirmTenant.lastName],
                ['טלפון נייד', confirmTenant.cellularPhone],
                ['טלפון בית', confirmTenant.homePhone],
                ['אימייל', confirmTenant.email],
                ['תאריך לידה', confirmTenant.dob],
                ['סוג דייר', TENANT_TYPE_MAP[confirmTenant.tenantType] || confirmTenant.tenantType],
                ['סטטוס', STATUS_MAP[confirmTenant.tenantStatus]?.label || confirmTenant.tenantStatus],
                ['% אחזקה', confirmTenant.percentageOfHOldingInApartment],
                ['איש קשר', confirmTenant.isContactPerson ? 'כן' : 'לא'],
                ['הערות', confirmTenant.tenantNotes],
              ].filter(([, v]) => v !== '' && v != null).map(([label, value]) => (
                <tr key={label}>
                  <td style={{ fontWeight: 600, paddingLeft: 16, paddingBottom: 4, color: '#374151' }}>{label}</td>
                  <td style={{ paddingBottom: 4 }}>{String(value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleConfirmAdd}>אישור — שמור דייר</button>
            <button className="btn btn-secondary" onClick={() => setConfirmTenant(null)}>חזור לעריכה</button>
          </div>
        </Modal>
      )}

      {deleteTenant && (
        <Modal title="מחיקת דייר" onClose={() => setDeleteTenant(null)}>
          <p style={{ fontSize: '1rem', marginBottom: 20 }}>
            האם אתה בטוח שברצונך למחוק את{' '}
            <strong>{deleteTenant.firstName} {deleteTenant.lastName}</strong>{' '}
            מדירה <strong>{deleteTenant.apartmentNumber}</strong>?
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary"
              style={{ background: '#991b1b', borderColor: '#991b1b' }}
              onClick={handleConfirmDelete}
            >
              כן, מחק
            </button>
            <button className="btn btn-secondary" onClick={() => setDeleteTenant(null)}>ביטול</button>
          </div>
        </Modal>
      )}

      {editTenant && (
        <Modal title={`עריכת דייר - ${editTenant.firstName} ${editTenant.lastName}`} onClose={() => setEditTenant(null)}>
          {googleDriveFolderId && (
            <a
              href={`https://drive.google.com/drive/folders/${googleDriveFolderId}?q=${editTenant.id}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block',
                marginBottom: 16,
                padding: '6px 14px',
                background: '#1a56db',
                color: '#fff',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontWeight: 600
              }}
            >
              תמונת תעודת זהות
            </a>
          )}
          <TenantForm initial={editTenant} isEdit onSubmit={handleSaveTenant} onCancel={() => setEditTenant(null)} />
          <hr style={{ margin: '20px 0', borderColor: '#e5e7eb' }} />
          <button
            className="btn btn-secondary"
            style={{ color: '#991b1b', borderColor: '#991b1b', fontSize: '0.85rem' }}
            onClick={() => { setDeleteTenant(editTenant); setEditTenant(null); }}
          >
            מחק דייר מדירה
          </button>
        </Modal>
      )}

      {/* Add Building */}
      {showAddBuilding && !confirmAddBuilding && (
        <Modal title="הוספת בניין חדש" onClose={() => { setShowAddBuilding(false); setAddBuildingError(''); }}>
          <form onSubmit={handleAddBuildingSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label>רחוב</label><input value={addBuildingForm.buildingAddress} onChange={e => { setAddBuildingError(''); setAddBuildingForm({ ...addBuildingForm, buildingAddress: e.target.value }); }} /></div>
              <div className="form-group"><label>מספר בניין</label><input value={addBuildingForm.buildingNumber} onChange={e => { setAddBuildingError(''); setAddBuildingForm({ ...addBuildingForm, buildingNumber: e.target.value }); }} /></div>
              <div className="form-group"><label>גוש</label><input type="number" value={addBuildingForm.parcel} onChange={e => { setAddBuildingError(''); setAddBuildingForm({ ...addBuildingForm, parcel: e.target.value }); }} /></div>
              <div className="form-group"><label>חלקה</label><input type="number" value={addBuildingForm.section} onChange={e => { setAddBuildingError(''); setAddBuildingForm({ ...addBuildingForm, section: e.target.value }); }} /></div>
              <div className="form-group"><label>שלב</label><input value={addBuildingForm.stage} onChange={e => { setAddBuildingError(''); setAddBuildingForm({ ...addBuildingForm, stage: e.target.value }); }} /></div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                  onClick={() => { setShowAddBuilding(false); setAddBuildingError(''); setShowTabu(true); setTabuParsed(null); setTabuMsg(''); }}>
                  📄 נסח טאבו
                </button>
              </div>
            </div>
            {addBuildingError && <div style={{ color: '#dc2626', fontSize: '0.88rem', marginTop: 8, fontWeight: 500 }}>⚠️ {addBuildingError}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" className="btn btn-primary">המשך</button>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowAddBuilding(false); setAddBuildingError(''); }}>ביטול</button>
            </div>
          </form>
        </Modal>
      )}
      {confirmAddBuilding && (
        <Modal title="אישור הוספת בניין" onClose={() => setConfirmAddBuilding(null)}>
          <p style={{ marginBottom: 16 }}>האם אתה בטוח שברצונך להוסיף בניין חדש?</p>
          <table style={{ fontSize: '0.88rem', marginBottom: 20, width: '100%' }}>
            <tbody>
              {[['רחוב', confirmAddBuilding.buildingAddress], ['מספר בניין', confirmAddBuilding.buildingNumber], ['גוש', confirmAddBuilding.parcel], ['חלקה', confirmAddBuilding.section], ['שלב', confirmAddBuilding.stage]].filter(([,v]) => v).map(([l,v]) => (
                <tr key={l}><td style={{ fontWeight: 600, paddingLeft: 16, paddingBottom: 4 }}>{l}</td><td style={{ paddingBottom: 4 }}>{v}</td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleConfirmAddBuilding}>אישור</button>
            <button className="btn btn-secondary" onClick={() => setConfirmAddBuilding(null)}>ביטול</button>
          </div>
        </Modal>
      )}

      {/* Lawyer Compare Modal */}
      {showLawyerCompare && (
        <Modal title={`השוואה עם עו"ד — ${selectedAddress} ${selectedNumber}`} onClose={() => setShowLawyerCompare(false)}>
          <div style={{ minWidth: 340 }}>
            <p style={{ marginBottom: 12, color: '#64748b', fontSize: '0.9rem' }}>
              העלה את האקסל שקיבלת מעורך הדין. המערכת תשווה רק את הבניין הנבחר.
            </p>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>קובץ אקסל (.xlsx)</label>
              <input type="file" accept=".xlsx,.xls" onChange={e => setLawyerFile(e.target.files[0])} />
            </div>
            {lawyerMsg && <div style={{ marginBottom: 12, color: lawyerMsg.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{lawyerMsg}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" disabled={!lawyerFile || lawyerComparing} onClick={async () => {
                setLawyerComparing(true); setLawyerMsg('');
                try {
                  const fd = new FormData();
                  fd.append('file', lawyerFile);
                  fd.append('buildingAddress', selectedAddress);
                  fd.append('buildingNumber', selectedNumber);
                  const res = await api.post('/reports/compare-lawyer', fd, { responseType: 'blob' });
                  if (res.headers['content-type']?.includes('json')) {
                    const text = await res.data.text();
                    const j = JSON.parse(text);
                    setLawyerMsg(j.message || 'שגיאה');
                  } else {
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'lawyer_compare.xlsx'; a.click();
                    URL.revokeObjectURL(url);
                    setLawyerMsg('✓ הדוח הורד בהצלחה');
                  }
                } catch (e) {
                  setLawyerMsg('שגיאה: ' + (e.response?.data?.error || e.message));
                } finally { setLawyerComparing(false); }
              }}>
                {lawyerComparing ? 'משווה...' : '⚖️ השווה והורד דוח'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowLawyerCompare(false)}>ביטול</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Tabu Import Modal */}
      {showTabu && (
        <Modal title="ייבוא נסח טאבו" onClose={() => setShowTabu(false)}>
          <div style={{ minWidth: 340 }}>
            {!tabuParsed && (
              <div>
                <p style={{ marginBottom: 12, color: '#374151', fontSize: '0.9rem' }}>העלה קובץ PDF של נסח טאבו — המערכת תחלץ אוטומטית גוש, חלקה, דירות ובעלים.</p>
                <label
                  style={{ display: 'block', background: '#f3f4f6', border: '2px dashed #d1d5db', borderRadius: 10, padding: '32px 24px', textAlign: 'center', cursor: tabuParsing ? 'default' : 'pointer', transition: 'border-color 0.2s' }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#6366f1'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#d1d5db'; const f = e.dataTransfer.files[0]; if (f) handleTabuFile({ target: { files: [f] } }); }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>📄</div>
                  {tabuParsing ? '⏳ מנתח את הנסח...' : 'גרור PDF לכאן או לחץ לבחירה'}
                  <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleTabuFile} disabled={tabuParsing} />
                </label>
                {tabuMsg && <div style={{ marginTop: 12, color: '#dc2626', fontSize: '0.88rem' }}>{tabuMsg}</div>}
              </div>
            )}
            {tabuParsed && (
              <div>
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: '0.85rem' }}>
                  <strong>דירות שחולצו:</strong> {tabuParsed.apartments?.length || 0}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div className="form-group">
                    <label>רחוב <span style={{ color: '#dc2626' }}>*</span></label>
                    <input value={tabuImportForm.buildingAddress} onChange={e => setTabuImportForm(f => ({ ...f, buildingAddress: e.target.value }))} placeholder="למשל: בלפור" />
                  </div>
                  <div className="form-group">
                    <label>מספר בניין <span style={{ color: '#dc2626' }}>*</span></label>
                    <input value={tabuImportForm.buildingNumber} onChange={e => setTabuImportForm(f => ({ ...f, buildingNumber: e.target.value }))} placeholder="למשל: 67" />
                  </div>
                  <div className="form-group">
                    <label>גוש <span style={{ color: '#dc2626' }}>*</span></label>
                    <input type="number" value={tabuImportForm.gush} onChange={e => setTabuImportForm(f => ({ ...f, gush: e.target.value }))} placeholder="למשל: 7148" />
                  </div>
                  <div className="form-group">
                    <label>חלקה <span style={{ color: '#dc2626' }}>*</span></label>
                    <input type="number" value={tabuImportForm.chelka} onChange={e => setTabuImportForm(f => ({ ...f, chelka: e.target.value }))} placeholder="למשל: 119" />
                  </div>
                </div>
                {tabuParsed.apartments?.length > 0 && (
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 16 }}>
                    <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>תת חלקה</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>בעלים</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>ת.ז</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tabuParsed.apartments.map(apt => (
                          apt.owners.length === 0
                            ? <tr key={apt.subParcel}><td colSpan={4} style={{ padding: '5px 10px', color: '#9ca3af' }}>{apt.subParcel} — אין בעלים</td></tr>
                            : apt.owners.map((o, i) => (
                              <tr key={`${apt.subParcel}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                {i === 0 && <td rowSpan={apt.owners.length} style={{ padding: '5px 10px', fontWeight: 600 }}>{apt.subParcel}</td>}
                                <td style={{ padding: '5px 10px' }}>{o.firstName} {o.lastName}</td>
                                <td style={{ padding: '5px 10px', direction: 'ltr' }}>{o.id}</td>
                                <td style={{ padding: '5px 10px' }}>{o.percentage != null ? o.percentage : '—'}</td>
                              </tr>
                            ))
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {tabuMsg && <div style={{ marginBottom: 12, color: tabuMsg.startsWith('✓') ? '#15803d' : '#dc2626', fontWeight: 600, fontSize: '0.88rem' }}>{tabuMsg}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" onClick={handleTabuImport} disabled={tabuImporting}>
                    {tabuImporting ? 'מייבא...' : '✓ ייבא לפרויקט'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => { setTabuParsed(null); setTabuMsg(''); }}>חזרה</button>
                  <button className="btn btn-secondary" onClick={() => setShowTabu(false)}>ביטול</button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Edit Building */}
      {showEditBuilding && !confirmEditBuilding && (
        <Modal title={`עריכת בניין — ${selectedAddress} ${selectedNumber}`} onClose={() => setShowEditBuilding(false)}>
          <form onSubmit={handleEditBuildingSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label>גוש</label><input type="number" value={editBuildingForm.parcel} onChange={e => setEditBuildingForm({ ...editBuildingForm, parcel: e.target.value })} /></div>
              <div className="form-group"><label>חלקה</label><input type="number" value={editBuildingForm.section} onChange={e => setEditBuildingForm({ ...editBuildingForm, section: e.target.value })} /></div>
              <div className="form-group"><label>שלב</label><input value={editBuildingForm.stage} onChange={e => setEditBuildingForm({ ...editBuildingForm, stage: e.target.value })} /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>קישור נסח טאבו (Google Drive)</label><input value={editBuildingForm.tabuUrl} onChange={e => setEditBuildingForm({ ...editBuildingForm, tabuUrl: e.target.value })} placeholder="https://drive.google.com/..." /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" className="btn btn-primary">המשך</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditBuilding(false)}>ביטול</button>
            </div>
          </form>
        </Modal>
      )}
      {confirmEditBuilding && (
        <Modal title="אישור עריכת בניין" onClose={() => setConfirmEditBuilding(null)}>
          <p style={{ marginBottom: 16 }}>האם אתה בטוח שברצונך לעדכן את בניין {selectedAddress} {selectedNumber}?</p>
          <table style={{ fontSize: '0.88rem', marginBottom: 20, width: '100%' }}>
            <tbody>
              {[['גוש', confirmEditBuilding.parcel], ['חלקה', confirmEditBuilding.section], ['שלב', confirmEditBuilding.stage]].map(([l,v]) => (
                <tr key={l}><td style={{ fontWeight: 600, paddingLeft: 16, paddingBottom: 4 }}>{l}</td><td style={{ paddingBottom: 4 }}>{v || '—'}</td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleConfirmEditBuilding}>אישור</button>
            <button className="btn btn-secondary" onClick={() => setConfirmEditBuilding(null)}>ביטול</button>
          </div>
        </Modal>
      )}

      {/* Add Apartment */}
      {showAddApt && !confirmAddApt && (
        <Modal title="הוספת דירה חדשה" onClose={() => setShowAddApt(false)}>
          <form onSubmit={handleAddAptSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label>מספר דירה</label><input type="number" value={addAptForm.apartmentNumber} onChange={e => setAddAptForm({ ...addAptForm, apartmentNumber: e.target.value })} required /></div>
              <div className="form-group"><label>קומה</label><input type="number" value={addAptForm.floor} onChange={e => setAddAptForm({ ...addAptForm, floor: e.target.value })} /></div>
              <div className="form-group"><label>תת חלקה</label><input type="number" value={addAptForm.subParcel} onChange={e => setAddAptForm({ ...addAptForm, subParcel: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" className="btn btn-primary">המשך</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddApt(false)}>ביטול</button>
            </div>
          </form>
        </Modal>
      )}
      {confirmAddApt && (
        <Modal title="אישור הוספת דירה" onClose={() => setConfirmAddApt(null)}>
          <p style={{ marginBottom: 16 }}>האם אתה בטוח שברצונך להוסיף דירה חדשה?</p>
          <table style={{ fontSize: '0.88rem', marginBottom: 20, width: '100%' }}>
            <tbody>
              {[['מספר דירה', confirmAddApt.apartmentNumber], ['קומה', confirmAddApt.floor], ['תת חלקה', confirmAddApt.subParcel]].filter(([,v]) => v).map(([l,v]) => (
                <tr key={l}><td style={{ fontWeight: 600, paddingLeft: 16, paddingBottom: 4 }}>{l}</td><td style={{ paddingBottom: 4 }}>{v}</td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleConfirmAddApt}>אישור</button>
            <button className="btn btn-secondary" onClick={() => setConfirmAddApt(null)}>ביטול</button>
          </div>
        </Modal>
      )}

      {/* Edit Apartment */}
      {editApt && !confirmEditApt && (
        <Modal title={`עריכת דירה ${editApt.apartmentNumber}`} onClose={() => setEditApt(null)}>
          <form onSubmit={handleEditAptSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label>קומה</label><input type="number" value={editAptForm.floor} onChange={e => setEditAptForm({ ...editAptForm, floor: e.target.value })} /></div>
              <div className="form-group"><label>תת חלקה</label><input type="number" value={editAptForm.subParcel} onChange={e => setEditAptForm({ ...editAptForm, subParcel: e.target.value })} /></div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 20, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><input type="checkbox" checked={editAptForm.isNorth} onChange={e => setEditAptForm({ ...editAptForm, isNorth: e.target.checked })} /> צפון</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><input type="checkbox" checked={editAptForm.isSouth} onChange={e => setEditAptForm({ ...editAptForm, isSouth: e.target.checked })} /> דרום</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><input type="checkbox" checked={editAptForm.isEast} onChange={e => setEditAptForm({ ...editAptForm, isEast: e.target.checked })} /> מזרח</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><input type="checkbox" checked={editAptForm.isWest} onChange={e => setEditAptForm({ ...editAptForm, isWest: e.target.checked })} /> מערב</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" className="btn btn-primary">המשך</button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditApt(null)}>ביטול</button>
            </div>
          </form>
        </Modal>
      )}
      {confirmEditApt && (
        <Modal title="אישור עריכת דירה" onClose={() => setConfirmEditApt(null)}>
          <p style={{ marginBottom: 16 }}>האם אתה בטוח שברצונך לעדכן דירה {confirmEditApt.apartmentNumber}?</p>
          <table style={{ fontSize: '0.88rem', marginBottom: 20, width: '100%' }}>
            <tbody>
              {[['קומה', confirmEditApt.floor], ['תת חלקה', confirmEditApt.subParcel], ['צפון', confirmEditApt.isNorth ? 'כן' : 'לא'], ['מזרח', confirmEditApt.isEast ? 'כן' : 'לא'], ['דרום', confirmEditApt.isSouth ? 'כן' : 'לא'], ['מערב', confirmEditApt.isWest ? 'כן' : 'לא']].map(([l,v]) => (
                <tr key={l}><td style={{ fontWeight: 600, paddingLeft: 16, paddingBottom: 4 }}>{l}</td><td style={{ paddingBottom: 4 }}>{v || '—'}</td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleConfirmEditApt}>אישור</button>
            <button className="btn btn-secondary" onClick={() => setConfirmEditApt(null)}>ביטול</button>
          </div>
        </Modal>
      )}

      {callApt && (
        <Modal title={`שיחות טלפון — דירה ${callApt.apartmentNumber}`} onClose={() => setCallApt(null)}>
          <form onSubmit={handleAddCall} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 200px' }}>
              <label>סיכום השיחה</label>
              <input
                value={callForm.phoneCallNote}
                onChange={e => setCallForm({ ...callForm, phoneCallNote: e.target.value })}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>תאריך מעקב</label>
              <DateInput
                name="followUpDate"
                value={callForm.followUpDate}
                onChange={e => setCallForm({ ...callForm, followUpDate: e.target.value })}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary">הוספה</button>
          </form>

          {callHistory.length === 0 ? <p style={{ color: '#9ca3af' }}>אין שיחות קודמות</p> : (
            <table style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th>תאריך השיחה</th>
                  <th>תוכן השיחה</th>
                  <th>תאריך מעקב</th>
                  <th>שם העובד</th>
                </tr>
              </thead>
              <tbody>
                {callHistory.map(c => (
                  <tr key={c.id}>
                    <td>{c.phoneCallDate ? formatDateDMY(c.phoneCallDate) : ''}</td>
                    <td>{c.phoneCallNote}</td>
                    <td>{c.followUpDate ? formatDateDMY(c.followUpDate) : ''}</td>
                    <td>{c.workerFirstName} {c.workerLastName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </div>
  );
}
